import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import categoryListApi from '../../../middleware/api/ExpenseManagement/Category/expenseCategoryListApi'
import {
  CountriesListResponse,
  CreditCardListResponse,
  EmpDepartmentListResponse,
  GetExpenseDetails,
  GetExpensePaymentList,
  GetpayAllAtOnceProps,
  PaymentList,
  PaymentListSliceState,
  ReimbursementExpenseList,
  ReimbursementExpenseListResponse,
  SavePaymentFormParams,
  employeePaymentStatus,
  getReimbursementExpenseListProps,
} from '../../../types/ExpenseManagement/PaymentList/PaymentListTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import { AppDispatch, RootState } from '../../../stateStore'
import { CategoryList } from '../../../types/ExpenseManagement/Category/categoryListTypes'
import paymentListApi from '../../../middleware/api/ExpenseManagement/PaymentList/PaymentListApi'

const getEmpDepartmentsList = createAsyncThunk(
  '/assetManagement/getEmpDepartments',
  async (_, thunkApi) => {
    try {
      return await paymentListApi.getDepartmentsDetails()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getCountriesList = createAsyncThunk(
  '/ExpenseManagement/getCountries',
  async (_, thunkApi) => {
    try {
      return await paymentListApi.getCountryDetails()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getCategoriesList = createAsyncThunk(
  '/ExpenseManagement/getCategoryList',
  async (_, thunkApi) => {
    try {
      return await categoryListApi.getExpenseCategories()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getCreditCardsDetails = createAsyncThunk(
  '/ExpenseManagement/getCardsList',
  async (_, thunkApi) => {
    try {
      return await paymentListApi.getCreditCardsList()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getReimbursementExpenseList = createAsyncThunk(
  'ExpenseManagement/getReimbursementExpenseList',
  async (props: getReimbursementExpenseListProps, thunkApi) => {
    try {
      return await paymentListApi.getReimbursementExpenseList(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getExpenseDetails = createAsyncThunk(
  'ExpenseManagement/getExpenseDetails',
  async (formId: number, thunkApi) => {
    try {
      return await paymentListApi.getExpenseDetailsById(formId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getpayAllAtOnce = createAsyncThunk<
  number[] | undefined,
  GetpayAllAtOnceProps,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'ExpenseManagement/payAllAtOnce',
  async (getpayAllAtOnceprops: GetpayAllAtOnceProps, thunkApi) => {
    try {
      return await paymentListApi.getpayAllAtOnce(getpayAllAtOnceprops)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getExpensePaymentList = createAsyncThunk(
  'ExpenseManagement/getExpensePaymentList',
  async (expenseId: number, thunkApi) => {
    try {
      return await paymentListApi.getExpensePaymentList(expenseId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const getPaymentList = createAsyncThunk(
  'ExpenseManagement/getPaymentList',
  async (_, thunkApi) => {
    try {
      return await paymentListApi.getPaymentList()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const savePaymentForm = createAsyncThunk(
  'ExpenseManagement/savePaymentForm',
  async (props: SavePaymentFormParams, thunkApi) => {
    try {
      return await paymentListApi.savePaymentForm(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const curMonth = 'Current Month'
const initialPaymentListState: PaymentListSliceState = {
  isLoading: ApiLoadingState.idle,
  isLoadingReimburse: ApiLoadingState.idle,
  error: null,
  paymentlist: [],
  reimbursementExpenseList: {} as ReimbursementExpenseListResponse,
  creditCardsList: [],
  selectedPaymentStatus: employeePaymentStatus.notpaid,
  reimbursementData: [],
  paymentListSize: 0,
  paymentListValue: curMonth,
  paymentTotalAmount: '',
  fromDate: '',
  toDate: '',
  getAllCategory: [],
  categoriesList: [],
  countriesList: [],
  expenseDetails: {} as GetExpenseDetails,
  empDepartments: [],
  getExpensePaymentList: [],
  getpayAllatOnce: {} as GetpayAllAtOnceProps,
  searchValue: '',
  pageSize: 20,
  pageNum: 1,

  departmentNameValue: '',
  categoryNameValue: '',
  countryValue: '',
  modeOfPaymentValue: '',
  creditCardsValue: '',
}
const paymentListSlice = createSlice({
  name: 'Payment',
  initialState: initialPaymentListState,
  reducers: {
    setMonthValue: (state, action) => {
      state.paymentListValue = action.payload
    },
    changeSelectedPaymentStatus: (state, action) => {
      state.selectedPaymentStatus = action.payload
    },
    clearPaymentList: (state) => {
      state.paymentListValue = 'Current Month'
    },
    setFromDate: (state, action) => {
      state.fromDate = action.payload
    },
    setToDate: (state, action) => {
      state.toDate = action.payload
    },
    setDepartmentValue: (state, action) => {
      state.departmentNameValue = action.payload
    },
    setCategoryNameValue: (state, action) => {
      state.categoryNameValue = action.payload
    },
    setCountryNameValue: (state, action) => {
      state.countryValue = action.payload
    },
    setModeOfPaymentValue: (state, action) => {
      state.modeOfPaymentValue = action.payload
    },
    setCreditCardValue: (state, action) => {
      state.creditCardsValue = action.payload
    },
    clearCheckBox: (state) => {
      state.reimbursementData = []
    },
    setSearchValue: (state, action) => {
      state.searchValue = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setPageNum: (state, action) => {
      state.pageNum = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCategoriesList.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getAllCategory = action.payload
      })
      .addCase(getCountriesList.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.countriesList = action.payload
      })
      .addCase(getEmpDepartmentsList.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.empDepartments = action.payload
      })
      .addCase(getReimbursementExpenseList.pending, (state) => {
        state.isLoadingReimburse = ApiLoadingState.loading
      })
      .addCase(getReimbursementExpenseList.fulfilled, (state) => {
        state.isLoadingReimburse = ApiLoadingState.succeeded
      })
      .addCase(getReimbursementExpenseList.rejected, (state) => {
        state.isLoadingReimburse = ApiLoadingState.failed
      })
      .addMatcher(isAnyOf(getPaymentList.fulfilled), (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.paymentlist = action.payload
      })
      .addMatcher(isAnyOf(getExpenseDetails.fulfilled), (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.expenseDetails = action.payload
      })
      .addMatcher(isAnyOf(getExpensePaymentList.fulfilled), (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.getExpensePaymentList = action.payload
      })
      .addMatcher(isAnyOf(getpayAllAtOnce.fulfilled), (state) => {
        state.isLoading = ApiLoadingState.succeeded
        // state.getpayAllatOnce = action.payload
      })
      .addMatcher(isAnyOf(getReimbursementExpenseList.pending), (state) => {
        state.isLoadingReimburse = ApiLoadingState.loading
      })
      .addMatcher(
        isAnyOf(getReimbursementExpenseList.fulfilled),
        (state, action) => {
          state.isLoading = ApiLoadingState.succeeded
          state.isLoadingReimburse = ApiLoadingState.succeeded
          state.paymentListSize = action.payload.size
          state.reimbursementData = action.payload.list
        },
      )
      .addMatcher(isAnyOf(getCreditCardsDetails.fulfilled), (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.creditCardsList = action.payload as CreditCardListResponse[]
      })
      .addMatcher(
        isAnyOf(
          getCountriesList.pending,
          getEmpDepartmentsList.pending,
          getCategoriesList.pending,
          getEmpDepartmentsList.pending,
          getPaymentList.pending,
          getCreditCardsDetails.pending,
          getExpenseDetails.pending,
          getExpensePaymentList.pending,
          getpayAllAtOnce.pending,
          getReimbursementExpenseList.pending,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.loading
        },
      )
      .addMatcher(
        isAnyOf(
          getCountriesList.rejected,
          getCategoriesList.rejected,
          getEmpDepartmentsList.rejected,
          getPaymentList.rejected,
          getCreditCardsDetails.rejected,
          getExpenseDetails.rejected,
          getExpensePaymentList.rejected,
          getpayAllAtOnce.rejected,
          getReimbursementExpenseList.rejected,
        ),
        (state) => {
          state.isLoading = ApiLoadingState.failed
        },
      )
  },
})

const isLoading = (state: RootState): LoadingState =>
  state.categoryList.isLoading

const departmentList = (state: RootState): EmpDepartmentListResponse[] =>
  state.paymentList.empDepartments
const countriesList = (state: RootState): CountriesListResponse[] =>
  state.paymentList.countriesList

const categories = (state: RootState): CategoryList[] =>
  state.categoryList.getAllCategory

const paymentlists = (state: RootState): PaymentList[] =>
  state.paymentList.paymentlist

const paymentStatus = (state: RootState): employeePaymentStatus =>
  state.paymentList.selectedPaymentStatus

const reimbursementExpenseList = (
  state: RootState,
): ReimbursementExpenseListResponse =>
  state.paymentList.reimbursementExpenseList

const creditCards = (state: RootState): CreditCardListResponse[] =>
  state.paymentList.creditCardsList

const getPayments = (state: RootState): ReimbursementExpenseListResponse =>
  state.paymentList.reimbursementExpenseList

const expensesList = (state: RootState): ReimbursementExpenseList[] =>
  state.paymentList.reimbursementData

const paymentsListSize = (state: RootState): number =>
  state.paymentList.paymentListSize

const paymentsAmount = (state: RootState): string =>
  state.paymentList.paymentTotalAmount

const expenseDetails = (state: RootState): GetExpenseDetails =>
  state.paymentList.expenseDetails

const getPaymentValue = (state: RootState): string | undefined =>
  state.paymentList.paymentListValue

const ExpensePaymentList = (state: RootState): GetExpensePaymentList[] =>
  state.paymentList.getExpensePaymentList

const payAllAtOnce = (state: RootState): GetpayAllAtOnceProps =>
  state.paymentList.getpayAllatOnce
const getFromDateValue = (state: RootState): string | Date =>
  state.paymentList.fromDate

const getToDateValue = (state: RootState): string | Date =>
  state.paymentList.toDate

const departmentNameValue = (state: RootState): string =>
  state.paymentList.departmentNameValue

const categoryNameValue = (state: RootState): string =>
  state.paymentList.categoryNameValue

const countryValue = (state: RootState): string =>
  state.paymentList.countryValue

const modeOfPaymentValue = (state: RootState): string =>
  state.paymentList.modeOfPaymentValue

const creditCardsValue = (state: RootState): string =>
  state.paymentList.creditCardsValue

const isLoadingReimburse = (state: RootState): LoadingState =>
  state.paymentList.isLoading

const getSearchValue = (state: RootState): string =>
  state.paymentList.searchValue

const getPageSize = (state: RootState): number => state.paymentList.pageSize

const getPageNum = (state: RootState): number => state.paymentList.pageNum

const paymentListThunk = {
  getEmpDepartmentsList,
  getCountriesList,
  getCategoriesList,
  getPaymentList,
  getCreditCardsDetails,
  getReimbursementExpenseList,
  getExpenseDetails,
  savePaymentForm,
  getExpensePaymentList,
  getpayAllAtOnce,
}

const paymentListSelectors = {
  isLoading,
  isLoadingReimburse,
  countriesList,
  departmentList,
  categories,
  paymentlists,
  creditCards,
  paymentStatus,
  reimbursementExpenseList,
  getPayments,
  expensesList,
  expenseDetails,
  paymentsListSize,
  paymentsAmount,
  getPaymentValue,
  getFromDateValue,
  getToDateValue,
  ExpensePaymentList,
  payAllAtOnce,
  departmentNameValue,
  modeOfPaymentValue,
  countryValue,
  categoryNameValue,
  creditCardsValue,
  getSearchValue,
  getPageSize,
  getPageNum,
}

export const paymentListService = {
  ...paymentListThunk,
  actions: paymentListSlice.actions,
  selectors: paymentListSelectors,
}

export default paymentListSlice.reducer
